import {
    ChangeDetectorRef,
    Component,
    forwardRef,
    Input,
    SimpleChanges
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import {
    applyResponsesToEntityRequiredDocumentation,
    AttachmentHandler,
    ConcreteMappedEntityConcreteFormDto,
    ConcreteRequiredEntityWorkflowAttachmentRequirement,
    CurrentAttachmentVersionDto,
    DisplayableMissionDocumentation,
    existsAndHasValue,
    findRequiringEntities,
    FormResponseCommand,
    FormResponseDto,
    initialiseResponse,
    PersonsOrganisationDto,
    RelatedEntityRequiredDocumentationAndResponses,
    updateOrAppendUnsubmittedResponse,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import deepmerge from 'deepmerge';
import { DocumentationDialoguesService } from 'libs/documentation/src/lib/documentation-dialogues.service';
import { DocumentationStorage } from 'libs/documentation/src/lib/interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    findActiveMissionApprovals,
    formAttachmentUrlFactory,
    getSimplifiedAuthorityFromApproval
} from '../../helpers';
import { MissionApprovalWithAuthority } from '../../interfaces';

interface InternalResponseLookup<T> {
    [entityKey: string]: { [responseEntityId: number]: T[] };
}

interface CompletableConcreteRequiredEntityWorkflowAttachmentRequirement
    extends ConcreteRequiredEntityWorkflowAttachmentRequirement {
    metMinimum?: boolean;
    metMaximum?: boolean;
    valid?: boolean;
    hasValue?: boolean;
}

interface CompletableRelatedEntityRequiredDocumentationAndResponses
    extends RelatedEntityRequiredDocumentationAndResponses {
    attachments: CompletableConcreteRequiredEntityWorkflowAttachmentRequirement[];
}

function mergeEntityDocumentationWithResponses(
    items: CompletableRelatedEntityRequiredDocumentationAndResponses[]
) {
    let result = items[0];
    for (const next of items.slice(1)) {
        result = Object.keys(next).reduce(
            (acc, step) => ({
                ...acc,
                [step]:
                    step in acc
                        ? Array.isArray(acc[step])
                            ? deepmerge(acc[step], next[step])
                            : acc[step] != null
                            ? typeof acc[step] === 'object'
                                ? deepmerge(acc[step], next[step])
                                : next[step]
                            : next[step]
                        : next[step]
            }),
            result
        );
    }
    return result;
}

export const MISSION_DOCUMENTATION_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MissionDocumentation),
    multi: true
};

export const MISSION_DOCUMENTATION_VALIDATOR: any = {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => MissionDocumentation),
    multi: true
};

@Component({
    selector: 'mission-documentation',
    templateUrl: './mission-documentation.component.html',
    styleUrls: ['../../styles.scss'],
    providers: [MISSION_DOCUMENTATION_VALIDATOR, MISSION_DOCUMENTATION_ACCESSOR]
})
export class MissionDocumentation implements ControlValueAccessor, Validator {
    @Input() organisation: PersonsOrganisationDto;
    /**
     * The title that is prefixed to the documentation to make it clear which state this applies to.
     */
    @Input() groupTitle: string;

    /**
     * The step identifier for the step this documentation applies to
     */
    @Input() step: string;

    @Input() documentationStorage: DocumentationStorage;

    @Input() readonly = false;

    @Input() attachmentsHandler: AttachmentHandler;

    @Input() prefillErrors: { [formId: number]: boolean };

    @Input() missionApprovalList?: MissionApprovalWithAuthority[];

    /**
     * The name of the workflow the mission is using, which is used to label the
     * block of mission workflow documentation.
     */
    @Input() missionWorkflowName?: string;

    working = false;

    workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    documentationResponses: CompletableRelatedEntityRequiredDocumentationAndResponses[];
    approvalDocumentationResponses: CompletableRelatedEntityRequiredDocumentationAndResponses;

    // These internal representations are used to do the actual rendering
    internalFormResponses: InternalResponseLookup<FormResponseDto>;

    activeApprovals: MissionApprovalWithAuthority[] = [];
    requiredFormsPerApproval: {
        [approvalId: number]: {
            [formId: number]: ConcreteMappedEntityConcreteFormDto;
        };
    } = {};
    requiredAttachmentsPerApproval: {
        [approvalId: number]: {
            [
                attachmentId: number
            ]: CompletableConcreteRequiredEntityWorkflowAttachmentRequirement;
        };
    } = {};

    getSimplifiedAuthorityFromApproval = getSimplifiedAuthorityFromApproval;

    private documentation: DisplayableMissionDocumentation;
    attachments: CurrentAttachmentVersionDto[];

    onChange: (value: any) => void;
    onTouched: () => void;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private documentationDialoguesService: DocumentationDialoguesService
    ) {}

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => {
                this.working = working;
                this.changeDetector.detectChanges();
            });

        if (this.missionApprovalList != null) {
            this.activeApprovals = findActiveMissionApprovals(
                this.missionApprovalList,
                true
            );
        }

        this.refresh(this.documentation);
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('attachmentsHandler' in changes) {
            if (this.attachmentsHandler != null) {
                this.attachmentsHandler.attachments$
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(attachments => {
                        this.attachments = attachments;
                        this.refresh(this.documentation);

                        // FIXME triggering validation
                        if (this.onChange != null) {
                            this.onChange(this.documentation);
                        }
                    });
            }
        }
        if (
            'missionApprovalList' in changes &&
            this.missionApprovalList != null
        ) {
            this.activeApprovals = findActiveMissionApprovals(
                this.missionApprovalList,
                true
            );
            // The documentation is grouped by approval, so it needs to be
            // rebuilt whenever the approvals change. The value can be written to
            // this control before the new approvals arrive, e.g. when a mission
            // is submitted and its approvals move out of draft.
            this.refresh(this.documentation);
        }
    }

    private refresh(documentation: DisplayableMissionDocumentation) {
        this.documentation = documentation;

        if (documentation == null || this.step == null) {
            this.documentationResponses = [];
            return;
        }

        // Because there are two iterated sections of the template, one for the mission workflow and one for approvals,
        // we need to seperate the mission workflow and approval entities so they can be handled separately.
        // We will create two arrays for this. The code below attempts to populate both arrays in a single filter function
        // in order to aid readability.

        // The array that will house the entities related to mission approvals that we will separate out
        const missionApprovalDocumentation = [];

        // Filter through the documentation entities to remove the approval entities and push them to the missionApprovalDocumentation array.
        const filteredMissionDocumentation =
            documentation.requiredDocumentation[this.step].filter(docs => {
                const missionApprovalIds = this.missionApprovalList?.map(
                    a => a.approval.id
                );
                if (missionApprovalIds != null) {
                    const approvalsMatchingDocs = missionApprovalIds.filter(
                        id =>
                            docs.forms?.find(
                                form => id === form.requiringEntityId
                            ) != null ||
                            docs.attachments?.find(
                                attachment =>
                                    id === attachment.requiringEntityId
                            ) != null ||
                            docs.checklists?.find(
                                checklist => id === checklist.requiringEntityId
                            ) != null
                    );
                    if (approvalsMatchingDocs.length > 0) {
                        missionApprovalDocumentation.push(docs);
                        return false;
                    }
                }
                return true;
            }, []);

        // From here there is some duplication as we run both arrays through similar additional logic.
        const requiringEntities = filteredMissionDocumentation.reduce(
            (acc, docs) => findRequiringEntities([docs.forms, acc]),
            []
        );

        const approvalRequiringEntities = missionApprovalDocumentation.reduce(
            (acc, docs) => findRequiringEntities([docs.forms, acc]),
            []
        );

        this.documentationResponses = filteredMissionDocumentation
            .map(r =>
                applyResponsesToEntityRequiredDocumentation(
                    r,
                    documentation.formResponses,
                    this.step,
                    requiringEntities
                )
            )
            .map(r => this.applyAttachmentStatuses(r));

        const missionApprovalResponses = missionApprovalDocumentation
            .map(r =>
                applyResponsesToEntityRequiredDocumentation(
                    r,
                    documentation.formResponses,
                    this.step,
                    approvalRequiringEntities
                )
            )
            .map(r => this.applyAttachmentStatuses(r));

        // Finally merge all approval entities into one object
        // This is so that the template can handle all approvals in one go.
        this.approvalDocumentationResponses =
            mergeEntityDocumentationWithResponses(missionApprovalResponses);

        // Build the lookups to easily group forms and attachments by approval.
        // The requiring entity of each item is the approval that requires it, so
        // the documentation itself says which approval to show each item under.
        // This is deliberately not sourced from the mission documentation DTO's
        // per approval documentation, as that is only populated for the
        // approvals that the current user has access to as an approver, and all
        // users can view the documentation of every approval on the mission.
        // An approval ID of -1 indicates mission forms not required by an approval.
        this.requiredFormsPerApproval = {};
        this.requiredAttachmentsPerApproval = {};

        const approvalIds = this.activeApprovals.map(a => a.approval.id);

        missionApprovalResponses.forEach(docs => {
            docs.forms.forEach(form => {
                const approvalId = form.requiringEntityId;
                if (
                    approvalId == null ||
                    form.entity?.formId == null ||
                    !approvalIds.includes(approvalId)
                ) {
                    return;
                }
                this.requiredFormsPerApproval[approvalId] = {
                    ...this.requiredFormsPerApproval[approvalId],
                    [form.entity.formId]: form
                };
            });
            docs.attachments.forEach(attachment => {
                const approvalId = attachment.requiringEntityId;
                if (
                    approvalId == null ||
                    attachment.entity?.id == null ||
                    !approvalIds.includes(approvalId)
                ) {
                    return;
                }
                this.requiredAttachmentsPerApproval[approvalId] = {
                    ...this.requiredAttachmentsPerApproval[approvalId],
                    [attachment.entity.id]: attachment
                };
            });
        });
    }

    private applyAttachmentStatuses(
        relatedEntity: RelatedEntityRequiredDocumentationAndResponses
    ): CompletableRelatedEntityRequiredDocumentationAndResponses {
        return {
            ...relatedEntity,
            attachments: relatedEntity.attachments.map(att => {
                const id = att.entity.id;
                const noAttached = this.attachments.filter(
                    a => a.requirementId === id
                ).length;
                return {
                    ...att,
                    metMinimum:
                        noAttached >= att.entity.minimumInstances
                            ? true
                            : false,
                    metMaximum:
                        noAttached === att.entity.maximumInstances
                            ? true
                            : false,
                    valid:
                        (att.entity.minimumInstances == null ||
                            noAttached >= att.entity.minimumInstances) &&
                        (att.entity.maximumInstances == null ||
                            noAttached <= att.entity.maximumInstances),
                    hasValue: noAttached > 0
                };
            })
        };
    }

    validate(control: FormControl): ValidationErrors | null {
        const displayableDocumentation: DisplayableMissionDocumentation =
            control.value;

        // The form control value will start off as undefined and can end up null, which will cause the
        // rest of the validation logic to throw.
        // Since individual documents may be required, but not the form control as a whole we can exit here if null
        if (displayableDocumentation == null) {
            return null;
        }

        const documentsForStep =
            displayableDocumentation.requiredDocumentation[this.step];

        const formsValid = documentsForStep.reduce(
            (acc, r) =>
                acc &&
                r.forms.reduce(
                    (fAcc, f) =>
                        fAcc &&
                        (displayableDocumentation.formResponses[
                            f.entity?.formId
                        ]?.find(
                            response =>
                                response.formVersionId ===
                                    f.entity.formVersionId &&
                                response.requiringEntityId ===
                                    f.requiringEntityId
                        )?.completed ||
                            !f.required),
                    true
                ) &&
                r.attachments.reduce(
                    (aAcc, a) =>
                        aAcc &&
                        (this.documentationResponses.find(doc =>
                            doc.attachments.find(
                                attachment =>
                                    attachment.requiringEntityId ===
                                        a.requiringEntityId &&
                                    attachment.entity.id === a.entity.id
                            )
                        ) != null
                            ? this.documentationResponses.find(
                                  doc =>
                                      doc.attachments.find(
                                          attachment =>
                                              attachment.requiringEntityId ===
                                                  a.requiringEntityId &&
                                              attachment.entity.id ===
                                                  a.entity.id
                                      ).valid
                              ) != null
                            : this.approvalDocumentationResponses.attachments.find(
                                  attachment =>
                                      attachment.requiringEntityId ===
                                          a.requiringEntityId &&
                                      attachment.entity.id === a.entity.id
                              )?.valid),
                    true
                ),

            true
        );

        return formsValid ? null : { required: true };
    }

    writeValue(documentation: DisplayableMissionDocumentation): void {
        this.refresh(documentation);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    showForm(
        entity: RelatedEntityRequiredDocumentationAndResponses,
        form: ConcreteMappedEntityConcreteFormDto
    ) {
        this.onTouched();

        const originalResponses: FormResponseDto[] = existsAndHasValue(
            entity.formResponses[form.entity.formId]
        )
            ? [this.documentation.formResponses[form.entity.formId][0]]
            : [];

        const buildFormAttachmentUrl = formAttachmentUrlFactory(
            form,
            this.activeApprovals.map(a => a.approval)
        );

        this.documentationDialoguesService.showFormInputDialogue(
            form,
            `${this.groupTitle} Form`,
            originalResponses,
            this.organisation.id,
            (response: FormResponseCommand) =>
                this.onFormSave(form.entity.formId, this.step, {
                    ...response,
                    relatedEntityType: entity.relatedEntityType,
                    relatedEntityId: entity.relatedEntityId
                }),
            buildFormAttachmentUrl
        );
    }

    /**
     * Show the responses to a form without allowing them to be changed. This is
     * used when the documentation can be viewed, but not edited.
     */
    showFormResponse(
        entity: RelatedEntityRequiredDocumentationAndResponses,
        form: ConcreteMappedEntityConcreteFormDto
    ) {
        // The responses are grouped by form, and for approvals they are merged
        // across all of the approvals, so find the ones for this requirement.
        const formResponses = (
            entity.formResponses[form.entity.formId] ?? []
        ).filter(
            response =>
                response.requiringEntityId === form.requiringEntityId &&
                response.requiringEntityType === form.requiringEntityType
        );

        const responses: FormResponseDto[] =
            formResponses.length > 0
                ? formResponses
                : [
                      initialiseResponse(
                          form,
                          entity.relatedEntityType,
                          entity.relatedEntityId
                      )
                  ];

        const buildFormAttachmentUrl = formAttachmentUrlFactory(
            form,
            this.activeApprovals.map(a => a.approval)
        );

        this.documentationDialoguesService.showFormResponseDialogue(
            responses,
            `${this.groupTitle} Form`,
            this.organisation.id,
            buildFormAttachmentUrl
        );
    }

    private onFormSave(
        formId: number,
        step: string,
        response: FormResponseCommand
    ) {
        if (this.documentationStorage != null) {
            return this.documentationStorage.storeFormResponse(
                formId,
                step,
                response
            );
        }

        const newFormResponses = {
            ...this.documentation.formResponses,
            [formId]: updateOrAppendUnsubmittedResponse(
                this.documentation.formResponses[formId],
                // @ts-ignore FIXME
                { ...response, step, submittedTime: null }
            )
        };

        const newDocumentation: DisplayableMissionDocumentation = {
            ...this.documentation,
            formResponses: newFormResponses
        };

        this.refresh(newDocumentation);
        this.onChange(newDocumentation);

        return Promise.resolve();
    }
}
